import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {InnerDrawerCount} from 'components/customer/Product/Drawer/InnerDrawerCount';
import {InnerDrawerCarcase} from 'components/customer/Product/Drawer/InnerDrawerCarcase';
import {InnerSelectedDrawer} from 'components/customer/Product/Drawer/InnerSelectedDrawer';
import {FaceDraftProvider} from 'components/customer/Product/Drawer/FaceDraftContext';
import {DragPreview} from 'components/customer/Product/Drawer/helpers/useInnerDrawerDrag';
import {useDrawerParams} from 'components/customer/Product/Drawer/helpers/useDrawerParams';
import {useDrawerAmount} from 'components/customer/Product/Drawer/helpers/useDrawerAmount';
import {useInnerDrawerPosition} from 'components/customer/Product/Drawer/helpers/useInnerDrawerPosition';
import {useInnerDrawerLimits} from 'components/customer/Product/Drawer/helpers/useInnerDrawerLimits';
import {
    InnerDrawerSpaces,
    Spaces,
} from 'components/customer/Product/Drawer/InnerDrawerSpaces';
import {useDrawerKeys} from 'components/customer/Product/Drawer/helpers/useDrawerKeys';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';
import {useDeleteInnerDrawer} from 'components/customer/Product/Drawer/helpers/useDeleteInnerDrawer';
import {FreeSpace} from 'components/customer/Product/Drawer/helpers/innerDrawerSlots';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {
    innerDrawerSummary,
    isCustomised,
} from 'components/customer/Product/Drawer/helpers/innerDrawerSummary';
import {useGetDrawerSystemQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {Icon} from 'shared/helpers';
import {
    Card,
    CardHeader,
    ResetButton,
} from 'components/customer/Product/Drawer/layoutStyles';

interface InnerDrawerLayoutProps {
    // the inner drawer_runners fieldset, which carries the type and runner names
    runnerFieldset: Fieldset;
    // the inner_drawers fieldset, which carries the drawer count's limits
    amountFieldset?: Fieldset;
}

const NO_SPACES: FreeSpace[] = [];
const NO_ROOM =
    'No room for another drawer. Move the drawers or pick smaller runners.';

const limit = (fieldset: Fieldset, option: string, fallback: number) => {
    const amount = fieldset?.fields?.find(
        (field) => field.name === 'drawer_amount'
    );
    const value = Number(amount?.options?.[String(option)]);

    return Number.isFinite(value) && value > 0 ? value : fallback;
};

// Auto shows the count and how the drawers are spaced; Edit layout is where
// single drawers are changed. Every drawer's own fields stay mounted in both,
// since they carry its type defaulting and runner lookup.
export const InnerDrawerLayout = ({
    runnerFieldset,
    amountFieldset,
}: InnerDrawerLayoutProps) => {
    const {values, setValues} = useFormikContext<DrawerFields>();
    const drawerParams = useDrawerParams(
        runnerFieldset?.fields?.[0],
        runnerFieldset
    );
    const {drawerAmount: heldAmount} = useDrawerAmount(runnerFieldset);
    // the form's own count, which the hook above only catches up with a
    // render later; a placed drawer must not lose its last block meanwhile
    const drawerAmount = toNumber(values.drawer_amount) || heldAmount;
    const {relayout} = useInnerDrawerPosition(true);
    // worked out once here, for the drawing and every drawer's field
    const limitsOf = useInnerDrawerLimits();
    const {resetRunners} = useDrawerDefaults();
    // only worked out while in Edit layout, by InnerDrawerSpaces below
    const [found, setFound] = useState<Spaces>(null);
    const spaces = found?.spaces ?? NO_SPACES;
    const {keys, insertKey, removeKey} = useDrawerKeys(
        values.drawers?.length ?? 0
    );
    const {canDelete, deleteDrawer} = useDeleteInnerDrawer();
    const [editing, setEditing] = useState(false);
    const [adding, setAdding] = useState(false);
    const compact = useCompactLayout();
    const {data: drawerSystems} = useGetDrawerSystemQuery();

    const drawers = useMemo(
        () => (values.drawers ?? []).slice(0, drawerAmount),
        [values.drawers, drawerAmount]
    );

    // the inner bottom clearance the bottom drawer's system puts it on
    const bottomClearance = useMemo(() => {
        const bottom = drawers[drawers.length - 1];
        const system = drawerSystems?.find(
            (drawerSystem) => drawerSystem.id == bottom?.insert_id
        );

        return typeof system?.innerDrawerBottomClearance === 'number'
            ? system.innerDrawerBottomClearance
            : null;
    }, [drawers, drawerSystems]);

    // A saved layout set by hand reopens in Edit layout, as Auto would hide
    // its positions and the count would re-space them. Decided once, and
    // settled by a count change made first: the customer is working in Auto.
    const decided = useRef(false);
    const settle = useCallback(() => {
        decided.current = true;
    }, []);

    useEffect(() => {
        if (decided.current) return;

        const customised = isCustomised(drawers, bottomClearance);

        if (customised === null) return;

        decided.current = true;

        if (customised) setEditing(true);
    }, [drawers, bottomClearance]);
    // the top drawer, as the faced layout opens on its top face
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [preview, setPreview] = useState<DragPreview>(null);

    // a drawer can be removed while it is selected
    const safeIndex = Math.min(selectedIndex, Math.max(0, drawerAmount - 1));

    const minimum = limit(amountFieldset, 'minimum', 1);
    const maximum = limit(amountFieldset, 'maximum', Infinity);

    const summary = useMemo(
        () => innerDrawerSummary(drawers, drawerParams.drawerRunnerFieldName),
        [drawers, drawerParams.drawerRunnerFieldName]
    );

    const editLayout = useCallback(() => setEditing(true), []);

    const atLimit = drawerAmount >= maximum;
    const canAdd = !atLimit && spaces.length > 0;
    const addTitle = adding
        ? 'Stop adding a drawer.'
        : atLimit
          ? `This product takes up to ${maximum} drawers.`
          : spaces.length
            ? 'Add a drawer in a free space. Nothing else moves.'
            : NO_ROOM;
    // shown, not only hovered; at the limit, room would not help
    const noRoom = !!found?.known && !adding && !atLimit && !spaces.length;

    // shows the free spaces on the drawing, where a click places a drawer
    const addHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            // a click leaves it unfocused; a key press keeps the focus
            if (event.detail > 0) event.currentTarget.blur();

            setAdding((on) => !on);
        },
        []
    );

    // the drawers and their count in one write, so neither the count's layout
    // nor validation sees one without the other
    const placeHandler = useCallback(
        (space: FreeSpace) => {
            if (!found) return;

            insertKey(space.index);
            void setValues({
                ...values,
                drawers: found.place(space),
                drawer_amount: drawerAmount + 1,
            });
            setSelectedIndex(space.index);
            setAdding(false);
        },
        [insertKey, setValues, values, found, drawerAmount]
    );

    useEffect(() => {
        if (!adding) return;

        const escape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setAdding(false);
        };

        document.addEventListener('keydown', escape);

        return () => document.removeEventListener('keydown', escape);
    }, [adding]);

    // from its box on the drawing or its panel alike
    const removeDrawer = useCallback(
        (index: number) => {
            removeKey(index);
            deleteDrawer(index);
            setAdding(false);
        },
        [removeKey, deleteDrawer]
    );

    // the spaces were worked out for the drawers as they were
    const selectHandler = useCallback((index: number) => {
        setSelectedIndex(index);
        setAdding(false);
    }, []);

    // as after a faced preset, every drawer goes back to the tallest runner its
    // new share takes, rather than keeping one chosen for a smaller space
    const backToAuto = useCallback(() => {
        if (!relayout()) return;

        resetRunners();
        setEditing(false);
        setAdding(false);
    }, [relayout, resetRunners]);

    return (
        <Card data-cy="inner-drawer-layout">
            <CardHeader>Inner Drawer Layout</CardHeader>

            {editing ? null : (
                <AutoBody data-cy="inner-drawer-auto">
                    <InnerDrawerCount
                        minimum={minimum}
                        maximum={maximum}
                        onChange={settle}
                    />
                    <CountRow>
                        <Summary>
                            <strong data-cy="inner-drawer-summary">
                                {summary}
                            </strong>
                            <span data-cy="inner-drawer-mode">Auto spaced</span>
                        </Summary>
                        <ModeButton
                            variant="link"
                            className="button-blue btn-sm"
                            data-cy="inner-drawer-edit-layout"
                            onClick={editLayout}>
                            <Icon iconName="Button-Edit.svg" color="white" />
                            <span>Edit layout</span>
                        </ModeButton>
                    </CountRow>
                </AutoBody>
            )}

            {editing ? (
                <InnerDrawerSpaces
                    drawerParams={drawerParams}
                    onChange={setFound}
                />
            ) : null}

            {/* mounted in Auto as well, hidden, for the drawers' own fields */}
            <EditBody hidden={!editing} data-cy="inner-drawer-edit">
                {/* the box on the drawing and the panel field show one half-typed position */}
                <FaceDraftProvider>
                    <Toolbar data-cy="inner-drawer-toolbar">
                        {/* a disabled button takes no pointer events, so its
                            reason and cursor sit on this instead */}
                        <AddWrap
                            title={addTitle}
                            data-cy="inner-drawer-add-reason"
                            $disabled={!adding && !canAdd}>
                            {/* Cancel as beside Add/Update product */}
                            <ResetButton
                                variant="link"
                                className={
                                    adding
                                        ? 'button-light btn-sm'
                                        : 'button-blue btn-sm'
                                }
                                data-cy="inner-drawer-add"
                                aria-pressed={adding}
                                disabled={!adding && !canAdd}
                                onClick={addHandler}>
                                <Icon
                                    iconName={
                                        adding
                                            ? 'Button-Cancel.svg'
                                            : 'Button-Add.svg'
                                    }
                                    color={adding ? '' : 'white'}
                                />
                                <span>{adding ? 'Cancel' : 'Add drawer'}</span>
                            </ResetButton>
                        </AddWrap>
                        <Count data-cy="inner-drawer-count-label">
                            {drawerAmount === 1
                                ? '1 drawer'
                                : `${drawerAmount} drawers`}
                        </Count>
                        <BackToAuto
                            variant="link"
                            className="button-blue btn-sm"
                            data-cy="inner-drawer-back-to-auto"
                            title="Go back to choosing a quantity; drawers are re-spaced automatically"
                            onClick={backToAuto}>
                            <Icon
                                iconName="Reverse-Refresh.svg"
                                color="white"
                            />
                            <span>Back to auto</span>
                        </BackToAuto>
                    </Toolbar>
                    <Drawing $compact={compact}>
                        {adding ? (
                            <Note data-cy="inner-drawer-adding-hint">
                                Click + on a space to add a drawer there, on the
                                runner it shows. Nothing else moves.
                            </Note>
                        ) : null}
                        {noRoom ? (
                            <NoRoom data-cy="inner-drawer-no-room">
                                {NO_ROOM}
                            </NoRoom>
                        ) : null}
                        <InnerDrawerCarcase
                            drawerParams={drawerParams}
                            selectedIndex={safeIndex}
                            onSelect={selectHandler}
                            preview={preview}
                            onPreview={setPreview}
                            limitsOf={limitsOf}
                            spaces={adding ? spaces : NO_SPACES}
                            onPlace={placeHandler}
                            canDelete={canDelete}
                            onDelete={removeDrawer}
                        />
                    </Drawing>
                    <InnerSelectedDrawer
                        drawerParams={drawerParams}
                        drawerAmount={drawerAmount}
                        selectedIndex={safeIndex}
                        preview={preview}
                        limitsOf={limitsOf}
                        keys={keys}
                        canDelete={canDelete}
                        onDelete={removeDrawer}
                    />
                </FaceDraftProvider>
            </EditBody>
        </Card>
    );
};

// its label stays on one line, as the summary can wrap instead
const ModeButton = styled(ResetButton)`
    &&& {
        flex-shrink: 0;
        white-space: nowrap;
    }
`;

const BackToAuto = styled(ModeButton)`
    &&& {
        margin-left: auto;
    }
`;

const AutoBody = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 18px;
    padding: 14px 18px;
    flex-wrap: wrap;
`;

// level with the count's field rather than its label; 40px is the field's height
const CountRow = styled.div`
    flex: 1 1 260px;
    min-width: 0;
    min-height: 40px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 18px;
`;

const Summary = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 12px;
    color: #6b7480;

    strong {
        font-weight: 600;
        color: ${({theme}) => theme.colors.primary?.main};
    }

    span {
        font-size: 11px;
    }
`;

// folded, the drawing takes the card's width up to this, with the panel under
// it; squeezed beside the panel, its boxes would have no room for their fields
const COMPACT_STAGE = 400;

const Drawing = styled.div<{$compact: boolean}>`
    display: flex;
    flex-direction: column;
    gap: 10px;
    ${({$compact}) =>
        $compact
            ? `flex: 0 1 ${COMPACT_STAGE}px; min-width: 0;`
            : 'flex: none;'}
`;

// a row of its own across the drawing and the panel, Back to auto at its end
const Toolbar = styled.div`
    flex: 1 0 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const AddWrap = styled.span<{$disabled: boolean}>`
    display: inline-flex;
    cursor: ${({$disabled}) => ($disabled ? 'not-allowed' : 'auto')};
`;

const Note = styled.div`
    max-width: 330px;
    font-size: 12px;
    color: #6b7480;
    text-wrap: pretty;
`;

const NoRoom = styled(Note)`
    font-size: 11px;
    line-height: 1.3;
`;

const Count = styled.span`
    font-size: 12px;
    color: #6b7480;
`;

const EditBody = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px 14px 20px;
    align-items: flex-start;
    flex-wrap: wrap;

    &[hidden] {
        display: none;
    }
`;
